import { Dispatch, SetStateAction, useState } from "react";

import EyeInvisibleOutlined from "@ant-design/icons/lib/icons/EyeInvisibleOutlined";
import EyeOutlined from "@ant-design/icons/lib/icons/EyeOutlined";
import { Form, Input } from "antd";
import zxcvbn from "zxcvbn";

import Error from "../Errors/Error";
import { Label } from "../Labels/Label";

const PasswordInput = ({
    required = false,
    origin = "signUp",
    autoFocus = false,
    error,
    name = "password",
    label = "Password",
    min = 8,
    setLoginErrorStatus,
    setPasswordError,
    handlePasswordChange = () => {},
}: {
    origin: "signUp" | "login" | "resetPassword" | "passwordSettings";
    required?: boolean;
    autoFocus?: boolean;
    error?: boolean;
    name?: string;
    label?: string;
    min?: number;
    setLoginErrorStatus?: Dispatch<SetStateAction<number | null>>;
    setPasswordError?: Dispatch<SetStateAction<boolean>>;
    handlePasswordChange?: (_password: string) => void;
}) => {
    const [passwordInputType, setPasswordInputType] = useState("password");
    const [password, setPassword] = useState("");
    const [passwordScore, setPasswordScore] = useState(0);
    const [passwordFeedback, setPasswordFeedback] = useState({
        warning: "",
        suggestions: [] as string[],
    });

    const strengthLabels = ["Too Weak", "Weak", "Fair", "Strong", "Very Strong"];
    const strengthColors = ["#ff4d4f", "#ff7a45", "#faad14", "#52c41a", "#389e0d"];

    const handleInputChange = (e: any) => {
        if (setLoginErrorStatus) {
            setLoginErrorStatus(null);
        }
        const newValue = e.target.value;
        setPassword(newValue);
        const result = zxcvbn(newValue);
        setPasswordScore(result.score);
        setPasswordFeedback({
            warning: result.feedback.warning || "",
            suggestions: result.feedback.suggestions || [],
        });
        setPasswordError?.(result.score < 3);
        handlePasswordChange?.(newValue);
    };

    return <div>
        <Form.Item
            hasFeedback
            name={name}
            required={required}
            label={<Label label={label} required={required}/>}
            {...(error ? { validateStatus: "error" } : {})}
            rules={[{
                required,
                message: <Error text={"Please input the password!"}/>,
            }, {
                min: min,
                message: <Error text={`Password must contain at least ${min} characters!`}/>,
            }]}>
            <Input
                autoFocus={autoFocus}
                type={passwordInputType}
                placeholder={"Enter your password"}
                data-testid={name}
                size="large"
                suffix={passwordInputType === "password"
                    ? <EyeInvisibleOutlined onClick={() => setPasswordInputType("text")}/>
                    : <EyeOutlined onClick={() => setPasswordInputType("password")}/>
                }
                onChange={handleInputChange}
                value={password}
            />

        </Form.Item>
        {/* Password Strength Indicator */}
        {origin !== "login" && password && (
            <div className="mb8">
                <div
                    style={{
                        background: strengthColors[passwordScore],
                        width: `${(passwordScore + 1) * 20}%`,
                    }}
                    className="signature-input__strength-bar"
                />
                <div
                    style={{
                        color: strengthColors[passwordScore],
                    }}
                    className="signature-input__strength-label"
                >
                    {strengthLabels[passwordScore]}
                </div>
            </div>
        )}

        {/* Feedback Text */}
        {origin !== "login" && (passwordFeedback.warning || passwordFeedback.suggestions.length > 0) && (
            <div className="signature-input__feedback">
                {passwordFeedback.warning && (
                    <div className="signature-input__feedback-warning">
                            ⚠️ {passwordFeedback.warning}
                    </div>
                )}
                {passwordFeedback.suggestions.map((s, i) => (
                    <div key={i}>• {s}</div>
                ))}
            </div>
        )}
    </div>;
};

export default PasswordInput;
